import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Eye, EyeOff, Mail, Lock, User, Calendar, Loader2, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { EmailVerificationModal } from './EmailVerificationModal';
import { Checkbox } from '@/components/ui/checkbox';
import * as storage from '@/lib/storage';
import { LoadingStateInline, LoadingState } from '@/components/ui/loading-state';
import { useBiometric, remoteLog } from '@/hooks/useBiometric';
import { Fingerprint } from 'lucide-react';
import { Capacitor } from '@capacitor/core';


interface LoginFormProps {
  onSuccess?: () => void;
}

type FormMode = 'email' | 'login' | 'register';

// Module-level mutex: prevents concurrent handleBiometricLogin executions
// even if the component remounts (e.g. brief re-render after onSuccess navigates).
let biometricLoginInProgress = false;

export const LoginForm = ({ onSuccess }: LoginFormProps) => {
  const [formMode, setFormMode] = useState<FormMode>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingBooking, setIsProcessingBooking] = useState(false);
  const [isProcessingOAuth, setIsProcessingOAuth] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // Added for confirm password visibility toggle
  const [emailCheckAttempts, setEmailCheckAttempts] = useState(0);
  const [showVerification, setShowVerification] = useState(false); // State for verification modal
  const [pendingRegistration, setPendingRegistration] = useState<any>(null); // Stores registration data while waiting for verification
  const [rememberMe, setRememberMe] = useState(true); // State for "Remember me" checkbox
  const { login, register, loginWithOAuth, setUser, setToken } = useAuth();
  const { toast } = useToast();
  const { isAvailable, authenticate, saveToken, getToken, hasStoredToken, deleteCredentials } = useBiometric();
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [pendingBiometricCallback, setPendingBiometricCallback] = useState<(() => void) | null>(null);
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isOAuthCallback = urlParams.get('success') === 'true' || urlParams.has('error');
    if (isOAuthCallback) return;

    const checkBiometric = async () => {
      const hardwareAvailable = await isAvailable();
      if (hardwareAvailable) {
        const tokenStored = await hasStoredToken();
        setBiometricAvailable(tokenStored);
      } else {
        setBiometricAvailable(false);
      }
    };
    checkBiometric();
  }, []);

  useEffect(() => {
    // sessionStorage flag persists across component remounts within the same browser session.
    // autoTriggeredRef (useRef) resets on every remount, causing duplicate auto-triggers.
    const alreadyTriggered = sessionStorage.getItem('biometricAutoTriggered') === 'true';
    if (biometricAvailable && !alreadyTriggered) {
      sessionStorage.setItem('biometricAutoTriggered', 'true');
      const platform = Capacitor.getPlatform();
      if (platform === 'android') {
        const timer = setTimeout(() => handleBiometricLogin(), 700);
        return () => clearTimeout(timer);
      } else {
        handleBiometricLogin();
      }
    }
  }, [biometricAvailable]);

  const offerBiometricOptIn = useCallback((afterCallback: () => void) => {
    const hasPendingBooking = sessionStorage.getItem('pendingBooking');
    if (hasPendingBooking) {
      afterCallback();
      return;
    }

    (async () => {
      const hardwareAvailable = await isAvailable();
      if (!hardwareAvailable) {
        afterCallback();
        return;
      }
      const alreadyStored = await hasStoredToken();
      if (alreadyStored) {
        afterCallback();
        return;
      }
      setPendingBiometricCallback(() => afterCallback);
      setShowBiometricPrompt(true);
    })();
  }, []);

  const handleBiometricOptInAccept = async () => {
    setShowBiometricPrompt(false);
    try {
      const authSuccess = await authenticate();
      if (authSuccess) {
        const userEmail = (() => {
          try {
            const u = localStorage.getItem('auth_user');
            return u ? JSON.parse(u).email : '';
          } catch { return ''; }
        })();
        const authToken = localStorage.getItem('auth_token');
        if (authToken && userEmail) {
          const response = await fetch('/api/auth/biometric-token', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
          });
          if (response.ok) {
            const data = await response.json();
            const saved = await saveToken(data.refreshToken, userEmail);
            if (saved) {
              toast({
                title: 'Biometric Login Enabled',
                description: 'You can now use fingerprint or Face ID to log in next time.',
              });
            }
          }
        }
      }
    } catch (e) {
      console.warn('[Biometric] Opt-in failed:', e);
    }
    pendingBiometricCallback?.();
    setPendingBiometricCallback(null);
  };

  const handleBiometricOptInDecline = () => {
    setShowBiometricPrompt(false);
    pendingBiometricCallback?.();
    setPendingBiometricCallback(null);
  };

  const handleBiometricLogin = async () => {
    if (biometricLoginInProgress) {
      remoteLog('warn', 'handleBiometricLogin called while already in progress — skipping duplicate');
      return;
    }
    biometricLoginInProgress = true;
    setIsLoading(true);
    setError('');
    remoteLog('log', '===== START handleBiometricLogin =====');
    try {
      // Step 1: biometric hardware prompt
      remoteLog('log', 'Step 1: calling authenticate()');
      const success = await authenticate();
      remoteLog('log', `Step 1 result: authenticate() = ${success}`);

      if (!success) {
        remoteLog('log', 'User cancelled or biometric failed — no error shown');
        return;
      }

      // Step 2: retrieve keychain credentials
      remoteLog('log', 'Step 2: calling getToken()');
      const tokenData = await getToken();
      remoteLog('log', tokenData ? `Step 2 result: found (email: ${tokenData.userEmail}, token length: ${tokenData.refreshToken?.length})` : 'Step 2 result: null');

      if (!tokenData) {
        remoteLog('warn', 'No stored credentials found in keychain');
        toast({
          title: 'No Saved Session',
          description: 'Please log in with your email and password first to enable biometric login.',
          variant: 'destructive',
        });
        setBiometricAvailable(false);
        return;
      }

      // Step 3: exchange biometric refresh token for a new session
      remoteLog('log', 'Step 3: POST /api/auth/refresh');
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ refreshToken: tokenData.refreshToken }),
        credentials: 'include',
      });
      remoteLog('log', `Step 3 result: refresh status = ${response.status} ${response.ok ? 'ok' : 'FAILED'}`);

      if (!response.ok) {
        const errBody = await response.text().catch(() => '(unreadable)');
        remoteLog('warn', `Refresh rejected — status: ${response.status}`, errBody);
        toast({
          title: 'Login Required',
          description: 'Please log in again to re-enable fingerprint login.',
        });
        await deleteCredentials();
        setBiometricAvailable(false);
        return;
      }

      // Step 4: parse response
      remoteLog('log', 'Step 4: parsing refresh response JSON');
      const data = await response.json();
      remoteLog('log', 'Step 4 result: data fields', {
        hasToken: !!data.token,
        hasRefreshToken: !!data.refreshToken,
        hasUser: !!data.user,
        userEmail: data.user?.email,
      });

      if (!data.token || !data.refreshToken || !data.user) {
        remoteLog('error', 'Incomplete data from refresh endpoint', { hasToken: !!data.token, hasRefreshToken: !!data.refreshToken, hasUser: !!data.user });
        setError('Session restore failed. Please log in again.');
        await deleteCredentials();
        setBiometricAvailable(false);
        return;
      }

      // Step 5: save new session tokens to localStorage
      remoteLog('log', 'Step 5: saving tokens to storage');
      await storage.saveToken(data.token);
      await storage.saveRefreshToken(data.refreshToken);
      await storage.saveUser(data.user);
      remoteLog('log', 'Step 5: storage saved');

      // Step 6: rotate biometric keychain token
      remoteLog('log', 'Step 6: rotating biometric keychain token');
      try {
        const biometricResponse = await fetch('/api/auth/biometric-token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${data.token}`,
          },
          credentials: 'include',
        });
        remoteLog('log', `Step 6: biometric-token status = ${biometricResponse.status}`);
        if (biometricResponse.ok) {
          const biometricData = await biometricResponse.json();
          if (biometricData.refreshToken) {
            const keychainSaved = await saveToken(biometricData.refreshToken, tokenData.userEmail);
            remoteLog(keychainSaved ? 'log' : 'error', `Step 6: keychain save result = ${keychainSaved}`);
          } else {
            remoteLog('warn', 'Step 6: biometric-token response had no refreshToken field');
          }
        } else {
          const body = await biometricResponse.text().catch(() => '');
          remoteLog('warn', `Step 6: biometric-token request failed, status: ${biometricResponse.status}`, body);
        }
      } catch (e: any) {
        remoteLog('warn', 'Step 6: exception rotating biometric token', { message: e?.message });
      }

      // Step 7: update React auth state
      remoteLog('log', 'Step 7: setUser / setToken');
      setUser(data.user);
      setToken(data.token);
      remoteLog('log', 'Step 7: auth state updated');

      // Step 8: success
      remoteLog('log', 'Step 8: showing toast and calling onSuccess');
      toast({
        title: 'Welcome back!',
        description: `Signed in as ${tokenData.userEmail}`,
      });
      onSuccess?.();
      remoteLog('log', '===== END handleBiometricLogin (success) =====');

    } catch (err: any) {
      remoteLog('error', '===== UNEXPECTED EXCEPTION =====', {
        name: err?.name,
        message: err?.message,
        stack: err?.stack,
      });
      setError('Biometric Login not available, please use other methods.');
    } finally {
      biometricLoginInProgress = false;
      setIsLoading(false);
    }
  };

  // Unified form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
  });

  const [userExists, setUserExists] = useState<boolean | null>(null);
  const [isOAuthOnly, setIsOAuthOnly] = useState(false);



  // This effect handles responses from the Google Sign-In SDK if used directly,
  // but the primary OAuth flow is now handled by URL parsing above.
  // It's kept here for potential future use or if a hybrid approach is intended.
  // For now, it might be redundant if the server handles all OAuth redirects.
  const response = null; // Placeholder, as the actual response object is not defined in this scope.
  const handleGoogleAuthResponse = (response: any) => {
    // This function would handle the response if using a direct SDK integration.
    // For server-side redirects, this is less relevant.
    console.log("Handling Google Auth Response (SDK):", response);
  };

  useEffect(() => {
    if (response?.type === 'success') {
      handleGoogleAuthResponse(response);
    } else if (response?.type === 'error') {
      // Alert.alert is typically for React Native. For web, use toast or setError.
      // Alert.alert('Error', 'Google login failed. Please try again.');
      setError('Google login failed. Please try again.');
    }
  }, [response]);


  // Check if email exists in database
  const checkEmailExists = async (email: string) => {
    if (emailCheckAttempts >= 4) {
      setError('Too many email checks. Please try again later.');
      return;
    }

    setIsCheckingEmail(true);
    setError('');

    try {
      console.log('[LoginForm] Checking email:', email);

      const response = await fetch('/api/auth/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          userType: 'client' // Client login modal always checks client users
        })
      });

      console.log('[LoginForm] Response status:', response.status);
      console.log('[LoginForm] Response headers:', Object.fromEntries(response.headers.entries()));

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('[LoginForm] Server returned non-JSON response');
        const text = await response.text();
        console.error('[LoginForm] Response text:', text.substring(0, 200));
        throw new Error('Server error - please try again or contact support');
      }

      const data = await response.json();
      console.log('[LoginForm] Response data:', data);

      setEmailCheckAttempts(prev => prev + 1);

      if (data.error) {
        // If it's a retryable database wake-up error, automatically retry after delay
        if (data.retryable && response.status === 503) {
          setError('Database is starting up, retrying automatically...');
          await new Promise(resolve => setTimeout(resolve, 10000)); // Wait 10 seconds
          setEmailCheckAttempts(0); // Reset attempts for automatic retry
          return checkEmailExists(email); // Retry
        }
        // Use the actual error message from the API
        setError(data.error);
        return;
      }

      if (data.exists) {
        setUserExists(true);
        setIsOAuthOnly(!data.hasPassword && data.hasOAuth);

        if (!data.hasPassword && data.hasOAuth) {
          setFormMode('email');
          setError('This account uses Google sign-in. Please use the "Sign in with Google" button above.');
        } else {
          setFormMode('login');
        }
      } else {
        // New user - clear any previous state and show registration form
        setUserExists(false);
        setIsOAuthOnly(false);
        setError('');
        setFormMode('register');
        console.log('[LoginForm] New user detected, switching to registration form');
      }
    } catch (error: any) {
      console.error('[LoginForm] Email check failed:', error);

      // Provide specific error messages based on error type
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        setError('Network error. Please check your internet connection and try again.');
      } else if (error.message?.includes('Server error')) {
        // Extract the actual server error message if available
        const match = error.message.match(/Server error \((\d+)\)/);
        if (match) {
          setError(`Server error (${match[1]}). Please try again in a moment.`);
        } else {
          setError('Server is temporarily unavailable. Please try again in a moment.');
        }
      } else if (error.message?.includes('non-JSON response')) {
        setError('Unexpected server response. Please try again or contact support if the issue persists.');
      } else {
        // Use the actual error message from the API
        setError(error.message || 'Unable to verify email address. Please try again.');
      }
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    checkEmailExists(formData.email);
  };

  const handleBackToEmail = () => {
    setFormMode('email');
    setUserExists(null);
    setIsOAuthOnly(false);
    setError('');
    setFormData(prev => ({ ...prev, password: '', confirmPassword: '', firstName: '', lastName: '', dateOfBirth: '' }));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const sanitizedEmail = formData.email.trim().toLowerCase();
    const result = await login(sanitizedEmail, formData.password, rememberMe);

    if (result.success) {
      if (result.isProcessingBooking) {
        console.log('🔄 Booking is being processed after login...');
        setIsProcessingBooking(true);

        const startTime = Date.now();
        const maxWaitTime = 10000;

        while (Date.now() - startTime < maxWaitTime) {
          await new Promise(resolve => setTimeout(resolve, 500));

          const redirectPath = sessionStorage.getItem('redirect_after_login');
          const pendingBooking = sessionStorage.getItem('pendingBooking');
          const bookingError = sessionStorage.getItem('booking_error');

          if (redirectPath && !pendingBooking) {
            console.log('✅ Booking processed successfully');
            setIsProcessingBooking(false);
            setIsLoading(false);
            onSuccess?.();
            return;
          }

          if (bookingError) {
            console.warn('⚠️ Booking processing failed:', bookingError);
            setIsProcessingBooking(false);
            setIsLoading(false);
            onSuccess?.();
            return;
          }
        }

        console.warn('⚠️ Booking processing timeout - proceeding anyway');
        sessionStorage.setItem('booking_error', 'Booking creation took too long. Please try again from your dashboard.');
        setIsProcessingBooking(false);
      }

      setIsLoading(false);
      offerBiometricOptIn(() => {
        onSuccess?.();
      });
    } else {
      setError(result.error || 'Login failed');
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    console.log('🔐 Starting registration process:', {
      email: formData.email,
      hasFirstName: !!formData.firstName,
      hasLastName: !!formData.lastName,
      hasPassword: !!formData.password,
      hasDOB: !!formData.dateOfBirth
    });

    // Use formData for validation
    if (formData.firstName.trim().length < 2) {
      console.warn('❌ First name validation failed:', formData.firstName.length);
      setError('First name must be at least 2 characters');
      return;
    }

    if (formData.lastName.trim().length < 2) {
      console.warn('❌ Last name validation failed:', formData.lastName.length);
      setError('Last name must be at least 2 characters');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      console.warn('❌ Passwords do not match');
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      console.warn('❌ Password too short:', formData.password.length);
      setError('Password must be at least 8 characters long');
      return;
    }

    console.log('✅ Client-side validation passed');
    setIsLoading(true);

    try {
      console.log('📨 Sending OTP request to:', '/api/auth/send-otp');

      // Send OTP to email with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      let otpResponse;
      try {
        otpResponse = await fetch('/api/auth/send-otp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email }),
          signal: controller.signal
        });
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          setError('Request timeout. Please try again.');
        } else {
          setError('Network error. Please check your connection.');
        }
        setIsLoading(false);
        return;
      }
      clearTimeout(timeoutId);

      console.log('📨 OTP response received:', {
        status: otpResponse.status,
        statusText: otpResponse.statusText,
        ok: otpResponse.ok
      });

      // Check content type before parsing
      const contentType = otpResponse.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('❌ Server returned non-JSON response:', {
          status: otpResponse.status,
          contentType: contentType,
          headers: Object.fromEntries(otpResponse.headers.entries())
        });

        // Try to read response text for debugging
        const text = await otpResponse.text();
        console.error('❌ Response body (first 500 chars):', text.substring(0, 500));

        setError('Server error - please refresh the page and try again.');
        setIsLoading(false);
        return;
      }

      let otpData;
      try {
        otpData = await otpResponse.json();
      } catch (parseError) {
        console.error('❌ Failed to parse JSON response:', parseError);
        setError('Invalid server response. Please try again.');
        setIsLoading(false);
        return;
      }

      console.log('📨 OTP response data:', {
        success: otpData.success,
        message: otpData.message,
        hasError: !!otpData.error
      });

      if (!otpResponse.ok) {
        console.error('❌ OTP request failed:', {
          status: otpResponse.status,
          message: otpData.message,
          error: otpData.error
        });
        setError(otpData.message || 'Failed to send verification code');
        setIsLoading(false);
        return;
      }

      console.log('✅ OTP sent successfully, showing verification modal');

      // Store registration data and show verification modal
      setPendingRegistration({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        dateOfBirth: formData.dateOfBirth,
      });
      setShowVerification(true);
      toast({
        title: 'Verification Code Sent',
        description: `A 6-digit code has been sent to ${formData.email}`,
      });
    } catch (err: any) {
      console.error('❌ Registration error:', {
        name: err.name,
        message: err.message,
        stack: err.stack
      });
      setError(err.message || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmail = async (code: string) => {
    console.log('🔐 Starting OTP verification:', {
      email: pendingRegistration.email,
      codeLength: code.length
    });

    setIsLoading(true);

    try {
      console.log('📨 Sending verification request to:', '/api/auth/verify-otp');

      // Verify OTP with backend
      const verifyResponse = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: pendingRegistration.email,
          code: code
        })
      });

      console.log('📨 Verification response received:', {
        status: verifyResponse.status,
        statusText: verifyResponse.statusText,
        ok: verifyResponse.ok
      });

      // Check content type before parsing
      const contentType = verifyResponse.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('❌ Server returned non-JSON response:', {
          status: verifyResponse.status,
          contentType: contentType,
          headers: Object.fromEntries(verifyResponse.headers.entries())
        });

        const text = await verifyResponse.text();
        console.error('❌ Response body (first 500 chars):', text.substring(0, 500));

        toast({
          title: 'Server Error',
          description: 'Unable to verify code. Please try again.',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      const verifyData = await verifyResponse.json();
      console.log('📨 Verification response data:', {
        success: verifyData.success,
        message: verifyData.message
      });

      if (!verifyResponse.ok) {
        console.error('❌ Verification failed:', {
          status: verifyResponse.status,
          message: verifyData.message
        });
        toast({
          title: 'Invalid Code',
          description: verifyData.message || 'The verification code is incorrect',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      console.log('✅ OTP verified, creating account...');

      // OTP verified - now create the account
      const result = await register(pendingRegistration, 'client');

      console.log('👤 Account creation result:', {
        success: result.success,
        hasUser: !!result.user,
        hasError: !!result.error
      });

      if (result.success) {
        console.log('✅ Registration completed successfully');

        // Check if booking is being processed
        if (result.isProcessingBooking) {
          console.log('🔄 Booking is being processed after registration...');
          setIsProcessingBooking(true);

          // Wait for booking processing with timeout (10 seconds total)
          const startTime = Date.now();
          const maxWaitTime = 10000; // 10 seconds

          while (Date.now() - startTime < maxWaitTime) {
            await new Promise(resolve => setTimeout(resolve, 500));

            // Check if booking completed or failed
            const redirectPath = sessionStorage.getItem('redirect_after_login');
            const pendingBooking = sessionStorage.getItem('pendingBooking');
            const bookingError = sessionStorage.getItem('booking_error');

            if (redirectPath && !pendingBooking) {
              console.log('✅ Booking processed successfully');
              setIsProcessingBooking(false);
              setShowVerification(false);
              onSuccess?.();
              return;
            }

            if (bookingError) {
              console.warn('⚠️ Booking processing failed:', bookingError);
              setIsProcessingBooking(false);
              setShowVerification(false);
              onSuccess?.(); // Still call onSuccess - user is registered and verified
              return;
            }
          }

          // Timeout reached
          console.warn('⚠️ Booking processing timeout - proceeding anyway');
          sessionStorage.setItem('booking_error', 'Booking creation took too long. Please try again from your dashboard.');
          setIsProcessingBooking(false);
        }

        setShowVerification(false);
        toast({
          title: 'Success!',
          description: 'Email verified and account created successfully',
        });
        offerBiometricOptIn(() => {
          onSuccess?.();
        });
      } else {
        console.error('❌ Account creation failed:', result.error);
        setError(result.error || 'Registration failed');
      }
    } catch (err: any) {
      console.error('❌ Verification error:', {
        name: err.name,
        message: err.message,
        stack: err.stack
      });
      setError(err.message || 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      const response = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pendingRegistration.email })
      });

      if (!response.ok) {
        throw new Error('Failed to resend code');
      }

      toast({
        title: 'Code Resent',
        description: 'A new verification code has been sent to your email.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to resend verification code',
        variant: 'destructive',
      });
    }
  };

  const handleCloseVerification = () => {
    setShowVerification(false);
    setPendingRegistration(null);
    setFormMode('register');
  };

  const handleOAuthLogin = () => {
    try {
      console.log('🔐 Google OAuth button clicked');

      // Get redirect path from sessionStorage or default priority order
      const redirectPath = sessionStorage.getItem('redirect_after_login') || '/my-account/sessions'; // Default to sessions page

      // Encode redirect path in OAuth state parameter with timestamp for server validation
      const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'
      const stateData = {
        redirect: redirectPath,
        csrf: Math.random().toString(36).substring(7), // CSRF protection
        timestamp: Date.now(), // For server-side expiry validation
        platform, // So server knows how to redirect back
      };
      const state = btoa(JSON.stringify(stateData)); // Use btoa instead of Buffer for browser compatibility

      // Store redirect path in sessionStorage as a backup
      sessionStorage.setItem('redirect_after_login', redirectPath);

      console.log('🔐 Starting OAuth with redirect:', redirectPath, 'platform:', platform);
      console.log('🔐 OAuth URL:', `/api/auth/google?redirect=${encodeURIComponent(redirectPath)}&state=${encodeURIComponent(state)}`);

      // Redirect to OAuth endpoint
      window.location.href = `/api/auth/google?redirect=${encodeURIComponent(redirectPath)}&state=${encodeURIComponent(state)}`;
    } catch (error) {
      console.error('❌ OAuth initiation error:', error);
      setError('Failed to start Google sign-in. Please try again.');
    }
  };

  return (
    <>
      {/* OAuth Processing Overlay */}
      {isProcessingOAuth && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm">
          <div className="text-center space-y-4">
            <div className="text-6xl animate-bounce">🦉</div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-gray-900">Welcome back!</h3>
              <p className="text-gray-600">Setting up your wellness journey...</p>
            </div>
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 bg-sage-600 rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-sage-600 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-sage-600 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      )}

      <EmailVerificationModal
        open={showVerification}
        email={pendingRegistration?.email || ''}
        onVerify={handleVerifyEmail}
        onResend={handleResendCode}
        onClose={handleCloseVerification}
      />

      <Dialog open={showBiometricPrompt} onOpenChange={(open) => { if (!open) handleBiometricOptInDecline(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Fingerprint className="w-5 h-5 text-sage-600" />
              Enable Biometric Login?
            </DialogTitle>
            <DialogDescription>
              Would you like to use fingerprint or Face ID to log in faster next time? You can change this later in your Security settings.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleBiometricOptInDecline}>
              Not Now
            </Button>
            <Button onClick={handleBiometricOptInAccept} className="bg-sage-600 hover:bg-sage-700">
              <Fingerprint className="w-4 h-4 mr-2" />
              Enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardDescription>
            {formMode === 'email' && 'Sign in to your account'}
            {formMode === 'login' && 'Welcome back! Enter your password'}
            {formMode === 'register' && 'Create your account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Conditional rendering of Booking Processing Loader */}
            {isProcessingBooking && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-8 max-w-md">
                  <LoadingState
                    context="bookings"
                    showSkeleton={false}
                  />
                  <p className="text-center text-sm text-muted-foreground mt-4">
                    Creating your session booking...
                  </p>
                </div>
              </div>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Google OAuth - Always visible */}
            <Button
              variant="outline"
              className="w-full"
              onClick={handleOAuthLogin}
              disabled={isLoading || isProcessingOAuth} // Disable if general OAuth processing or login is ongoing
              type="button"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign in or register with Google
            </Button>

            {biometricAvailable && (
              <Button
                variant="outline"
                className="w-full"
                onClick={handleBiometricLogin}
                disabled={isLoading}
                type="button"
              >
                <Fingerprint className="w-4 h-4 mr-2" />
                Sign in with Biometrics
              </Button>
            )}

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
              </div>
            </div>

            {/* Email Entry Form */}
            {formMode === 'email' && (
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      className="pl-10"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      disabled={isCheckingEmail}
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isCheckingEmail}>
                  {isCheckingEmail ? (
                    <div className="flex items-center gap-2">
                      <span className="text-base" style={{ animation: 'owl-blink 3s ease-in-out infinite' }}>🦉</span>
                      <span>Checking your wellness profile...</span>
                    </div>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </form>
            )}

            {/* Login Form */}
            {formMode === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="login-email"
                      type="email"
                      className="pl-10"
                      value={formData.email}
                      disabled
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent text-xs text-green-600"
                      onClick={handleBackToEmail}
                    >
                      Change
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Your password"
                      className="pl-10 pr-10"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      autoFocus
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="flex items-center space-x-2 mb-4">
                  <Checkbox
                    id="remember-me"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                  />
                  <label
                    htmlFor="remember-me"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Remember me
                  </label>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Logging in...</> : 'Login'}
                </Button>

                <div className="text-center">
                  <Button
                    variant="link"
                    className="text-sm text-green-600 hover:text-green-700 p-0 h-auto"
                    onClick={() => window.location.href = '/forgot-password'}
                  >
                    Forgot Password?
                  </Button>
                </div>
              </form>
            )}

            {/* Registration Form */}
            {formMode === 'register' && (
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="register-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="register-email"
                      type="email"
                      className="pl-10"
                      value={formData.email}
                      disabled
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent text-xs text-green-600"
                      onClick={handleBackToEmail}
                    >
                      Change
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="firstName"
                        placeholder="Tisya"
                        className="pl-10"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        autoComplete="given-name"
                        required
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      placeholder="Singhal"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      autoComplete="family-name"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="register-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="At least 8 characters"
                      className="pl-10 pr-10"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      autoComplete="new-password"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      autoComplete="new-password"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dateOfBirth">Date of Birth</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="dateOfBirth"
                      type="date"
                      className="pl-10"
                      value={formData.dateOfBirth}
                      onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                      autoComplete="bday"
                      required
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
};

// Helper function to poll for booking completion
const waitForBookingCompletion = async (): Promise<void> => {
  return new Promise(resolve => {
    const checkInterval = setInterval(() => {
      const pendingBooking = sessionStorage.getItem('pendingBooking');
      // If pending booking is gone and no error is set, assume completion.
      if (!pendingBooking && !sessionStorage.getItem('booking_error')) {
        clearInterval(checkInterval);
        resolve();
      }
      // Add a timeout to prevent infinite loops if something goes wrong
      // though the server-side logic should ideally handle this.
    }, 500); // Check every 500ms
  });
};